import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import {
  Breadcrumb,
  browserProfilingIntegration,
  browserTracingIntegration,
  init,
  replayIntegration,
} from '@sentry/angular';
import { environment } from '@env';
import { version } from '../version.json';
import { AppModule } from './app/app.module';
import { monitorLogger } from './app/project/monitor/monitor-logger';
import {
  handlerLocalStorageChange,
  handlerDOMContentLoaded,
  handlerPermissionListener,
} from './app/project/monitor/handlers';

function filterSentryBreadcrumb(breadcrumb: Breadcrumb) {
  const isAgoraVisibilityNoise = breadcrumb.category === 'console' && breadcrumb.message?.includes('visibility');

  const isAngularFormWarning =
    breadcrumb.level === 'warning' && breadcrumb.message?.includes('reactive form directive');

  if (isAgoraVisibilityNoise || isAngularFormWarning) {
    return null;
  }

  return breadcrumb;
}

function initializeSentry() {
  const SENTRY_ENABLED_ENVIRONMENTS = ['production'];
  const sentryDsn = environment.environmentVariables.sentryDsn;
  const currEnvironment = environment.currentEnvironment || 'production';

  const shouldEnable = SENTRY_ENABLED_ENVIRONMENTS.includes(currEnvironment) && !!sentryDsn;

  init({
    // Enable Sentry only in specified environments
    enabled: shouldEnable,
    // Set the DSN (Data Source Name) for your Sentry project
    dsn: sentryDsn,
    // Set the environment that the app is running in
    environment: currEnvironment,
    // Set the release to the app version
    release: version,
    // Send Personally Identifiable Information
    sendDefaultPii: false,
    // Whether to enable debug mode
    debug: false,
    // Set traces capture rate
    tracesSampleRate: 0.2,
    // Set replays capture rates
    replaysSessionSampleRate: 0.0,
    // Set replays capture rate for sessions that have an error
    replaysOnErrorSampleRate: 0.2,
    // Set profiling data capture rate
    profilesSampleRate: 0.2,

    // Enable tracing integration
    enableTracing: true,

    integrations: [
      // Enable Sentry's performance monitoring integration
      browserTracingIntegration(),
      // Enable Sentry's session replay integration
      replayIntegration({ blockAllMedia: false }),
      // Enable Sentry's profiling integration
      browserProfilingIntegration(),
    ],
    beforeBreadcrumb: filterSentryBreadcrumb,
  });
}
initializeSentry();

if (environment.production) {
  enableProdMode();
  monitorLogger.initializeAnalytics('production');
} else {
  monitorLogger.initializeAnalytics('debug');
}

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));

handlerDOMContentLoaded();
handlerPermissionListener();
handlerLocalStorageChange();
